import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CrudTable = ({ data, entityType, config, language, onEdit, onDelete, onView }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(25);
  const [sortField, setSortField] = useState(config?.tableColumns?.[0]?.key || 'name');
  const [sortDirection, setSortDirection] = useState('asc');

  const labels = {
    actions: { en: 'Actions', ar: 'الإجراءات' },
    rowsPerPage: { en: 'Rows per page:', ar: 'الصفوف لكل صفحة:' },
    previous: { en: 'Previous', ar: 'السابق' },
    next: { en: 'Next', ar: 'التالي' },
    of: { en: 'of', ar: 'من' }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getNestedValue = (obj, path) => {
    return path?.split('.')?.reduce((acc, part) => acc?.[part], obj);
  };

  const sortedData = [...data]?.sort((a, b) => {
    const aVal = getNestedValue(a, sortField);
    const bVal = getNestedValue(b, sortField);
    if (sortDirection === 'asc') {
      return aVal > bVal ? 1 : -1;
    }
    return aVal < bVal ? 1 : -1;
  });

  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = sortedData?.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(sortedData?.length / recordsPerPage);

  const renderCellValue = (item, column) => {
    const value = getNestedValue(item, column?.key);

    if (column?.array && Array.isArray(value)) {
      return (
        <div className="flex flex-wrap gap-1">
          {value?.slice(0, 2)?.map((v, i) => (
            <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
              {v}
            </span>
          ))}
          {value?.length > 2 && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-text-secondary">
              +{value?.length - 2}
            </span>
          )}
        </div>
      );
    }

    if (column?.badge) {
      return (
        <span className="inline-flex items-center px-2 py-1 md:px-3 md:py-1.5 rounded-full text-xs md:text-sm font-medium bg-primary/10 text-primary">
          {value}
        </span>
      );
    }

    if (column?.price) {
      return <span className="font-semibold text-success">${parseFloat(value)?.toFixed(2)}</span>;
    }

    return <span className="text-xs md:text-sm text-text-secondary">{value}</span>;
  };

  return (
    <div className="overflow-hidden">
      <div className="overflow-x-auto scrollbar-custom">
        <table className="w-full min-w-[600px]">
          <thead className="bg-muted border-b border-border">
            <tr>
              {config?.tableColumns?.map((column) => (
                <th key={column?.key} className="px-4 py-3 md:px-6 md:py-4 text-left">
                  {column?.sortable ? (
                    <button
                      onClick={() => handleSort(column?.key)}
                      className="flex items-center gap-2 text-xs md:text-sm font-semibold text-foreground hover:text-primary transition-smooth"
                    >
                      {column?.label?.[language]}
                      <Icon
                        name={sortField === column?.key ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'}
                        size={16}
                      />
                    </button>
                  ) : (
                    <span className="text-xs md:text-sm font-semibold text-foreground">
                      {column?.label?.[language]}
                    </span>
                  )}
                </th>
              ))}
              <th className="px-4 py-3 md:px-6 md:py-4 text-center text-xs md:text-sm font-semibold text-foreground">
                {labels?.actions?.[language]}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {currentRecords?.map((item) => (
              <tr key={item?.id} className="hover:bg-muted/50 transition-smooth">
                {config?.tableColumns?.map((column) => (
                  <td key={column?.key} className="px-4 py-3 md:px-6 md:py-4">
                    {column?.key === config?.tableColumns?.[0]?.key ? (
                      <p className="text-sm md:text-base font-medium text-foreground">{getNestedValue(item, column?.key)}</p>
                    ) : (
                      renderCellValue(item, column)
                    )}
                  </td>
                ))}
                <td className="px-4 py-3 md:px-6 md:py-4">
                  <div className="flex items-center justify-center gap-2">
                    <button
                      onClick={() => onView(item)}
                      className="p-1.5 md:p-2 rounded-lg hover:bg-primary/10 text-primary transition-smooth"
                      aria-label="View item"
                    >
                      <Icon name="Eye" size={16} />
                    </button>
                    <button
                      onClick={() => onEdit(item)}
                      className="p-1.5 md:p-2 rounded-lg hover:bg-warning/10 text-warning transition-smooth"
                      aria-label="Edit item"
                    >
                      <Icon name="Edit2" size={16} />
                    </button>
                    <button
                      onClick={() => onDelete(item)}
                      className="p-1.5 md:p-2 rounded-lg hover:bg-error/10 text-error transition-smooth"
                      aria-label="Delete item"
                    >
                      <Icon name="Trash2" size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-3 md:px-6 md:py-4 border-t border-border bg-muted/30">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span className="text-xs md:text-sm text-text-secondary caption">{labels?.rowsPerPage?.[language]}</span>
            <select
              value={recordsPerPage}
              onChange={(e) => {
                setRecordsPerPage(Number(e?.target?.value));
                setCurrentPage(1);
              }}
              className="px-2 py-1 md:px-3 md:py-1.5 rounded-lg border border-border bg-background text-xs md:text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs md:text-sm text-text-secondary caption">
              {indexOfFirstRecord + 1}-{Math.min(indexOfLastRecord, data?.length)} {labels?.of?.[language]} {data?.length}
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                iconName="ChevronLeft"
                iconSize={16}
              >
                <span className="hidden sm:inline">{labels?.previous?.[language]}</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                iconName="ChevronRight"
                iconPosition="right"
                iconSize={16}
              >
                <span className="hidden sm:inline">{labels?.next?.[language]}</span>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CrudTable;